/**
 * Babylon.js engine wrapper — keeps the canvas and scene around for the
 * whole session (engines are expensive to create), gives child components
 * the live `Scene` via context.
 */

import {
  ArcRotateCamera,
  Color3,
  Color4,
  DirectionalLight,
  Engine,
  HemisphericLight,
  MeshBuilder,
  Scene,
  ShadowGenerator,
  StandardMaterial,
  Vector3,
  Texture,
} from '@babylonjs/core';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

interface EngineCtx {
  scene: Scene | null;
  engine: Engine | null;
}

const Ctx = createContext<EngineCtx>({ scene: null, engine: null });

export function useScene() {
  return useContext(Ctx);
}

interface Props {
  children?: ReactNode;
  /** True when the world canvas should run; false during HTML-only stages saves GPU. */
  active: boolean;
}

export function BabylonEngine({ children, active }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<Engine | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;

    // antialias=true on desktop, false on mobile to save bandwidth.
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    const engine = new Engine(canvas, !isMobile, {
      preserveDrawingBuffer: false,
      stencil: false,
      adaptToDeviceRatio: true,
      powerPreference: 'high-performance',
    });
    engineRef.current = engine;
    // R220: adaptive hardware scaling по реальному разрешению окна.
    // На 2K/4K рендерим в native (1.0) для резкости. На FHD 1.1, ниже — 1.25.
    const computeHwScale = (): number => {
      if (isMobile) return 1.5;
      const px = window.innerWidth * window.innerHeight;
      if (px >= 3840 * 2160 * 0.9) return 1.0;            // 4K
      if (px >= 2560 * 1440 * 0.85) return 1.0;            // 2K/QHD
      if (px >= 1920 * 1080 * 0.85) return 1.1;            // FHD
      return 1.25;                                          // меньше
    };
    engine.setHardwareScalingLevel(computeHwScale());
    // Expose for combat-text projection helper.
    (window as unknown as { __aiwariEngine?: Engine }).__aiwariEngine = engine;

    const scene = new Scene(engine);

    // R232q86zh7: ?perf=1 — lightweight scene profiler for prod probes.
    // Publishes draw calls / active meshes / fps to window.__aiwariPerf
    // every second. Costs nothing when the flag is off.
    try {
      if (new URLSearchParams(window.location.search).get('perf') === '1') {
        void import('@babylonjs/core/Instrumentation/sceneInstrumentation.js').then(({ SceneInstrumentation }) => {
          const inst = new SceneInstrumentation(scene);
          inst.captureActiveMeshesEvaluationTime = true;
          inst.captureRenderTargetsRenderTime = true;
          const perf: Record<string, number> = {};
          (window as unknown as { __aiwariPerf?: Record<string, number> }).__aiwariPerf = perf;
          const timer = window.setInterval(() => {
            perf.fps = Math.round(engine.getFps() * 10) / 10;
            perf.drawCalls = (inst as unknown as { drawCallsCounter?: { current: number } }).drawCallsCounter?.current ?? -1;
            perf.activeMeshes = scene.getActiveMeshes().length;
            perf.totalMeshes = scene.meshes.length;
            perf.totalMaterials = scene.materials.length;
            perf.animRunning = scene.animationGroups.filter((g) => g.isPlaying).length;
            perf.evalMs = Math.round(inst.activeMeshesEvaluationTimeCounter.lastSecAverage * 100) / 100;
          }, 1000);
          scene.onDisposeObservable.add(() => window.clearInterval(timer));
        });
      }
    } catch { /* profiler is optional */ }
    sceneRef.current = scene;
    // R232q80: тёплый heraldic чёрный (~#0F0900) вместо холодного синего —
    // пока terrain не загружен, пустая сцена сочетается с HUD, а не диссонирует.
    scene.clearColor = new Color4(0.058, 0.035, 0.0, 1);
    // R232: fogDensity 0.008 → 0.0001 — раньше всё за 11м было размыто.
    // Юзер: "мутно, расплывчато всё видно". Теперь дальняя видимость >300м.
    // Atmosphere.ts override этого при загрузке зон (там свои значения).
    scene.fogMode = Scene.FOGMODE_EXP2;
    scene.fogColor = new Color3(0.05, 0.07, 0.12);
    // R232q5h3: matches Atmosphere.ts ~600m fog-of-war.
    scene.fogDensity = 0.0018;

    // R232q86r: NEUTRAL lighting — earlier warm sun tint + warm ground bounce
    // + warm emissive overlap → каждая skin texture рендерилась как bronze.
    // Sun = pure white, hemi ground = neutral grey, ambient = neutral grey.
    scene.ambientColor = new Color3(0.5, 0.5, 0.55);
    const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
    hemi.intensity = 0.9;
    hemi.diffuse = new Color3(1, 1, 1);
    hemi.groundColor = new Color3(0.3, 0.3, 0.3);

    const sun = new DirectionalLight('sun', new Vector3(-0.5, -1, -0.3), scene);
    sun.intensity = 1.0;
    sun.diffuse = new Color3(1, 1, 1);
    sun.specular = new Color3(0, 0, 0);
    // R232q54: re-enable ShadowGenerator with a HARD 8-mesh caster cap.
    // Lessons learned from R232q5h2: ShadowGenerator(2048) used to shader-
    // recompile every visible mesh (~23k objects) → empty blue screen on
    // boot.  This time we use 1024 hard shadows + an explicit caster
    // allowlist that World.tsx populates dynamically (player + 5 closest
    // NPCs + 2 closest mobs). Map size 1024 keeps memory at 4 MB and the
    // single shadow pass is one extra draw call per caster.
    const shadowGen = new ShadowGenerator(1024, sun);
    // Hard shadows (no PCSS / blur kernel) — fixed 1-tap depth comparison
    // is the cheapest possible variant and stays under the 5ms budget.
    shadowGen.usePoissonSampling = false;
    shadowGen.useExponentialShadowMap = false;
    shadowGen.useBlurExponentialShadowMap = false;
    shadowGen.bias = 0.0008;
    shadowGen.normalBias = 0.02;
    shadowGen.darkness = 0.55;
    // Stash the generator on the scene so World.tsx can attach casters
    // without re-importing the constructor or re-creating the generator.
    (scene as unknown as { __aiwariShadowGen?: ShadowGenerator }).__aiwariShadowGen = shadowGen;
    // Sun disc — small emissive sphere anchored at the inverted sun-light
    // direction × 4000m so it reads as a celestial body, not a mesh.
    // applyFog/infiniteDistance keeps the disc visible at all camera
    // distances (matches sky-dome behaviour).
    const sunDisc = MeshBuilder.CreateSphere('sun-disc', { diameter: 220, segments: 16 }, scene);
    const sunMat = new StandardMaterial('sun-disc-mat', scene);
    sunMat.emissiveColor = new Color3(1.0, 0.92, 0.55);
    sunMat.diffuseColor = new Color3(0, 0, 0);
    sunMat.specularColor = new Color3(0, 0, 0);
    sunMat.disableLighting = true;
    sunDisc.material = sunMat;
    sunDisc.isPickable = false;
    sunDisc.applyFog = false;
    sunDisc.infiniteDistance = true;
    sunDisc.renderingGroupId = 0;
    // Anchor at -sunDir × 4000 (the sun is OPPOSITE the light's travel dir).
    const sunOffset = sun.direction.scale(-4000);
    sunDisc.position.set(sunOffset.x, sunOffset.y, sunOffset.z);

    // R232q5h Visual: rim light — back-light from opposite hemisphere to
    // pop character silhouettes against the sky. Cheap (no shadow cast),
    // intensity low so it kisses edges without flattening the scene.
    const rim = new DirectionalLight('rim', new Vector3(0.6, -0.5, 0.7), scene);
    rim.intensity = 0.55;
    rim.diffuse = new Color3(0.85, 0.92, 1.0);
    rim.specular = new Color3(0.5, 0.55, 0.65);

    // Default camera — child components can replace with a follow rig.
    // R232q86zc: closer default radius 8→5 + lower beta π/3→π/2.5 so the
    // body fills more of the frame on first paint. Юзер сообщил character
    // выглядит крошечным — это camera distance, не mesh size.
    const cam = new ArcRotateCamera('default-cam', -Math.PI / 2, Math.PI / 2.5, 5, new Vector3(0, 1.0, 0), scene);
    cam.attachControl(canvas, true);
    cam.lowerRadiusLimit = 2.5;
    cam.upperRadiusLimit = 30;
    cam.wheelDeltaPercentage = 0.01;

    // R232q81 (MVP visual baseline): replace the 50m placeholder disc with
    // a textured 400m ground + a vertex-coloured gradient skybox so the
    // canvas is never a flat clearColor, even before World.tsx mounts or
    // when ADT/Atmosphere fail to load. World.tsx still creates the real
    // 7000×3500 ground; this engine-level disc sits at y=-0.2 so it loses
    // z-fight to the real ground in the player area but remains visible
    // beyond it as a horizon filler. Renamed to 'engine-ground' so World's
    // `getMeshByName('ground').dispose()` does not remove it.
    const ground = MeshBuilder.CreateDisc('engine-ground', { radius: 400, tessellation: 96 }, scene);
    ground.rotation.x = Math.PI / 2;
    ground.position.y = -0.2;
    const groundMat = new StandardMaterial('engine-ground-mat', scene);
    // Heraldic warm-stone — matches the warm-black clearColor and the brand
    // palette. Tiles the existing grass-tile.png if it loads, falls back
    // to solid colour if it 404s.
    groundMat.diffuseColor = new Color3(0.34, 0.30, 0.20);
    groundMat.specularColor = new Color3(0, 0, 0);
    groundMat.emissiveColor = new Color3(0.06, 0.05, 0.03);
    {
      const tex = new Texture(
        `${import.meta.env.BASE_URL}textures/grass-tile.png`,
        scene,
        false, false, undefined,
        () => { tex.uScale = 24; tex.vScale = 24; groundMat.diffuseTexture = tex; },
        () => { /* keep solid colour */ },
      );
      tex.uScale = 24;
      tex.vScale = 24;
    }
    ground.material = groundMat;
    ground.receiveShadows = true;

    // Gradient skydome — 600m radius sphere with per-vertex colour going
    // warm-orange at horizon → deep slate at zenith. Renders behind every
    // mesh, never writes depth. Stays in scene even after Atmosphere.ts
    // installs its own dome (Atmosphere puts its dome at 4000m and uses
    // renderingGroupId 0, so this 600m fallback is overdrawn on first
    // ATL frame and disposed in cleanup if Atmosphere succeeds).
    const sky = MeshBuilder.CreateSphere('engine-sky', { diameter: 1200, segments: 24, sideOrientation: 1 }, scene);
    sky.isPickable = false;
    sky.applyFog = false;
    sky.infiniteDistance = true;
    sky.renderingGroupId = 0;
    {
      const positions = sky.getVerticesData('position');
      const colours: number[] = [];
      if (positions) {
        for (let i = 0; i < positions.length; i += 3) {
          const y = positions[i + 1] ?? 0;
          // Normalise -600..+600 to 0..1 (0 = south pole, 1 = north pole).
          const t = Math.max(0, Math.min(1, (y / 600 + 1) / 2));
          // Warm horizon (#3A2410) up to dusty slate (#1A1F2A) at zenith.
          const r = 0.22 * (1 - t) + 0.10 * t;
          const g = 0.14 * (1 - t) + 0.12 * t;
          const b = 0.06 * (1 - t) + 0.16 * t;
          colours.push(r, g, b, 1);
        }
        sky.setVerticesData('color', colours, false, 4);
      }
    }
    const skyMat = new StandardMaterial('engine-sky-mat', scene);
    skyMat.disableLighting = true;
    skyMat.emissiveColor = new Color3(1, 1, 1);
    skyMat.specularColor = new Color3(0, 0, 0);
    skyMat.backFaceCulling = false;
    skyMat.disableDepthWrite = true;
    sky.material = skyMat;
    sky.useVertexColors = true;

    // R232q5al: feed camera position into AudioManager listener once per
    // frame so positional SFX (mob hits, distant doors) pan + attenuate
    // relative to where the player is looking. Throttled to ~10 Hz to
    // avoid Howler overhead — pan changes that fast aren't perceivable.
    let lastListenerUpdate = 0;
    void import('../audio/AudioManager.js').then(({ audio }) => {
      // initial pos
      audio.setListener(cam.position.x, cam.position.y, cam.position.z);
      const onBefore = (): void => {
        const now = performance.now();
        if (now - lastListenerUpdate < 100) return;
        lastListenerUpdate = now;
        const target = scene.activeCamera ?? cam;
        const p = target.position;
        audio.setListener(p.x, p.y, p.z);
      };
      scene.onBeforeRenderObservable.add(onBefore);
    }).catch(() => { /* AudioManager optional during failover */ });

    // Render loop.
    engine.runRenderLoop(() => {
      if (sceneRef.current) sceneRef.current.render();
    });

    const onResize = () => {
      // R220: пересчитать hwScale при resize (юзер мог растянуть на 2K экран).
      engine.setHardwareScalingLevel(computeHwScale());
      engine.resize();
    };
    window.addEventListener('resize', onResize);
    // R220: initial resize — на случай если canvas размер был 0 на mount.
    engine.resize();
    // R220: наблюдаем за canvas сам — например при devtools open/close.
    const ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(() => engine.resize()) : null;
    if (ro) ro.observe(canvas);
    setReady(true);

    return () => {
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
      scene.dispose();
      engine.dispose();
      sceneRef.current = null;
      engineRef.current = null;
      delete (window as unknown as { __aiwariEngine?: Engine }).__aiwariEngine;
    };
  }, []);

  return (
    <Ctx.Provider value={{ scene: sceneRef.current, engine: engineRef.current }}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          touchAction: 'none',
          opacity: active ? 1 : 0,
          transition: 'opacity 0.4s',
          pointerEvents: active ? 'auto' : 'none',
        }}
      />
      {ready && children}
    </Ctx.Provider>
  );
}
